import { useMemo, useEffect } from "react";
import {
  formatNumber,
  getCountryFlagByCity,
  getCountryInfo,
} from "../../../helper/utility";
import StreamIcon from "../../../assets/images/icons/stream.svg";
import FanbaseIcon from "../../../assets/images/icons/fanbase.svg";
import BestTrack from "../../../assets/images/icons/best-track.svg";
import Listeners from "../../../assets/images/icons/listeners.svg";
import FollowersIcon from "../../../assets/images/icons/followers.svg";
import UserIcon from "../../../assets/images/icons/user-logo.png";
import LineChartIcon from "../../../assets/images/line-chart.svg";
import AppleIcon from "../../../assets/images/admin/Apple.svg";
import StartIcon from "../../../assets/images/admin/Star.svg";
import AmazonIcon from "../../../assets/images/admin/Amazon.svg";
import SoundcloudIcon from "../../../assets/images/admin/Group-line.svg";
import InstaIcon from "../../../assets/images/admin/Instagram.png";
import TiktokIcon from "../../../assets/images/admin/Tiktok.png";
import YoutubeIcon from "../../../assets/images/admin/Youtube.png";
import SpotifyLineIcon from "../../../assets/images/admin/spotify-lines.svg";
import RadioPlayIcon from "../../../assets/images/admin/Radio-Play.png";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";

countries.registerLocale(en);

const fillMissingValues = (data, key) => {
  let lastValue = null;

  return data?.map(entry => {
    if (entry[key] === 0 || entry[key] === null || entry[key] === undefined) {
      return lastValue !== null ? { ...entry, [key]: lastValue } : entry;
    } else {
      lastValue = entry[key];
      return entry;
    }
  });
};

const useMusicSourceData = (artistData, chartData, period, source) => {
  const trackData =
    "topTrackData" in (artistData || {})
      ? artistData?.topTrackData
      : artistData?.topTrack;

  /*For all and spotify data */
  const streamData = fillMissingValues(chartData?.totalStreams, 'streams_total')
  const streamChart = useMemo(
    () => ({
      name: "Total Streams",
      categories: streamData?.map((entry) => entry.date) || [],
      data: streamData?.map((entry) => entry.streams_total) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const followersData = fillMissingValues(chartData?.totalFollowers, 'followers_total')
  const followersChart = useMemo(
    () => ({
      name: "Total Followers",
      categories: followersData?.map((entry) => entry.date) || [],
      data:
        followersData?.map((entry) => entry.followers_total) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const listenersData = fillMissingValues(chartData?.totalListeners, 'monthly_listeners_current')
  const listenersChart = useMemo(
    () => ({
      name: "Total listeners",
      categories: listenersData?.map((entry) => entry.date) || [],
      data:
        listenersData?.map(
          (entry) => entry.monthly_listeners_current
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const fanbaseData = fillMissingValues(chartData?.totalFanbase, 'fanbase_total')
  const fanbaseChart = useMemo(
    () => ({
      name: "Total Fanbase",
      categories: fanbaseData?.map((entry) => entry.date) || [],
      data: fanbaseData?.map((entry) => entry.fanbase_total) || [],
      intervalType: period,
    }),
    [chartData]
  );
  /*End For all and spotify data */

  /*For Apple Music Data */
  const PlaylistsAddApple = useMemo(
    () => ({
      name: "Total Playlists Adds Apple",
      categories:
        (chartData?.playlist_add || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.playlist_add || [])?.map((entry) => entry.playlist_add) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const TotalChartedApple = useMemo(
    () => ({
      name: "Total Charted Apple",
      categories:
        (chartData?.total_charted || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.total_charted || [])?.map((entry) => entry.total_charted) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const TotalChartedITunes = useMemo(
    () => ({
      name: "Total Charted iTunes",
      categories:
        (chartData?.total_charted_itunes || [])?.map((entry) => entry.date) ||
        [],
      data:
        (chartData?.total_charted_itunes || [])?.map(
          (entry) => entry.total_charted_itunes
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const ApplePlaylistsAndCharts = useMemo(
    () => ({
      name: "Total Playlists Charts Apple",
      categories:
        (chartData?.topPlaylistsCharts || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.topPlaylistsCharts || [])?.map(
          (entry) => entry.topPlaylistsCharts
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );
  /*End For Apple Music Data */

  /*For Amazon Data */
  const followersChartAmazon = useMemo(
    () => ({
      name: "Total Followers Amazon",
      categories: chartData?.totalFollowers?.map((entry) => entry.date) || [],
      data:
        chartData?.totalFollowers?.map((entry) => entry.followers_total) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const TopPlaylistsAddAmazon = useMemo(
    () => ({
      name: "Top Playlists Adds Amazon",
      categories:
        (chartData?.playlist_add || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.playlist_add || [])?.map((entry) => entry.playlist_add) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const TotalChartedAmazon = useMemo(
    () => ({
      name: "Total Charted Amazon",
      categories:
        (chartData?.total_charted || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.total_charted || [])?.map((entry) => entry.total_charted) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const AmazonPlaylistsAndCharts = useMemo(
    () => ({
      name: "Total Playlists Charts Amazon",
      categories:
        (chartData?.topPlaylistsCharts || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.topPlaylistsCharts || [])?.map(
          (entry) => entry.topPlaylistsCharts
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );
  /*End For Amazon Data */

  /*For Soundcloud Data */
  const streamDataSoundCloud = fillMissingValues(chartData?.totalStreams, 'streams_total')
  const streamChartSoundCloud = useMemo(
    () => ({
      name: "Total Streams SoundCloud",
      categories: streamDataSoundCloud?.map((entry) => entry.date) || [],
      data: streamDataSoundCloud?.map((entry) => entry.streams_total) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const followersDataSoundCloud = fillMissingValues(chartData?.totalFollowers, 'followers_total')
  const followersChartSoundCloud = useMemo(
    () => ({
      name: "Total Followers SoundCloud",
      categories: followersDataSoundCloud?.map((entry) => entry.date) || [],
      data:
        followersDataSoundCloud?.map((entry) => entry.followers_total) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const TotalChartedSoundCloud = useMemo(
    () => ({
      name: "Total Charted SoundCloud",
      categories:
        (chartData?.total_charted || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.total_charted || [])?.map((entry) => entry.total_charted) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const favoritesDataSoundCloud = fillMissingValues(chartData?.total_favorites, 'total_favorites')
  const TotalFavoritesChart = useMemo(
    () => ({
      name: "Total Fovorites SoundCloud",
      categories:
        (favoritesDataSoundCloud || [])?.map((entry) => entry.date) || [],
      data:
        (favoritesDataSoundCloud || [])?.map(
          (entry) => entry.total_favorites
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const repostsData = fillMissingValues(chartData?.total_reposts, 'reposts_total')
  const totalRepostsChart = useMemo(
    () => ({
      name: "Total Reposts SoundCloud",
      categories:
        (repostsData || [])?.map((entry) => entry.date) || [],
      data:
        (repostsData || [])?.map((entry) => entry.reposts_total) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );
  /*End For Soundcloud Data */
  const allMetrics = useMemo(
    () => [
      {
        icon: StreamIcon,
        label: "Streams",
        value: formatNumber(artistData?.totalStreams || 0),
        chartData: streamChart?.data?.length
          ? streamChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label:
          period == "all" || period == "yearly"
            ? "Total Listeners"
            : period == "monthly" || period == "weekly"
              ? "Peak Listeners"
              : "",
        value:
          period == "all" || period == "yearly"
            ? formatNumber(artistData?.totalListeners || 0)
            : period == "monthly" || period == "weekly"
              ? formatNumber(artistData?.currentListeners || 0)
              : 0,
        chartData: listenersChart?.data?.length
          ? listenersChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Best Performing Track",
        value: formatNumber(trackData?.streamsCount || 0),
        trackName: trackData?.track_name || "No track",
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: Listeners,
        label:
          period === "weekly" || period === "monthly"
            ? "Current Listeners"
            : period === "all" || period === "yearly"
              ? "Peak Listeners"
              : "",
        value:
          period === "weekly" || period === "monthly"
            ? formatNumber(artistData?.currentListeners || 0)
            : period === "all" || period === "yearly"
              ? formatNumber(artistData?.peakListeners || 0)
              : 0,
        chartData: listenersChart?.data?.length
          ? listenersChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FollowersIcon,
        label: "Followers",
        value: formatNumber(artistData?.totalFollowers || 0),
        chartData: followersChart?.data?.length
          ? followersChart
          : { name: "No Data", categories: [], data: [] },
      },
    ],
    [
      artistData,
      chartData,
      streamChart,
      fanbaseChart,
      listenersChart,
      followersChart,
      period,
    ]
  );

  const spotifyMetrics = useMemo(
    () => [
      {
        icon: StreamIcon,
        label: "Streams",
        value: formatNumber(artistData?.totalStreams || 0),
        chartData: streamChart?.data?.length
          ? streamChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label:
          period == "all" || period == "yearly"
            ? "Total Listeners"
            : period == "monthly" || period == "weekly"
              ? "Peak Listeners"
              : "",
        value:
          period == "all" || period == "yearly"
            ? formatNumber(artistData?.totalListeners || 0)
            : period == "monthly" || period == "weekly"
              ? formatNumber(artistData?.currentListeners || 0)
              : 0,
        chartData: fanbaseChart?.data?.length
          ? fanbaseChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Best Performing Track",
        value: formatNumber(trackData?.streamsCount || 0),
        trackName: trackData?.track_name || "No track",
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: Listeners,
        label:
          period === "weekly" || period === "monthly"
            ? "Current Listeners"
            : period === "all" || period === "yearly"
              ? "Peak Listeners"
              : "",
        value:
          period === "weekly" || period === "monthly"
            ? formatNumber(artistData?.currentListeners || 0)
            : period === "all" || period === "yearly"
              ? formatNumber(artistData?.peakListeners || 0)
              : 0,
        chartData: listenersChart?.data?.length
          ? listenersChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FollowersIcon,
        label: "Followers",
        value: formatNumber(artistData?.totalFollowers || 0),
        chartData: followersChart?.data?.length
          ? followersChart
          : { name: "No Data", categories: [], data: [] },
      },
    ],
    [
      artistData,
      chartData,
      streamChart,
      fanbaseChart,
      listenersChart,
      followersChart,
      period,
    ]
  );

  const AppleMusicMetrics = useMemo(
    () => [
      {
        icon: StreamIcon,
        label: "Playlists Add",
        value: formatNumber(
          artistData?.playlist_add
            ? artistData?.playlist_add || 0
            : artistData?.playlists_add || 0
        ),
        score: formatNumber(
          artistData?.playlist_add
            ? artistData?.playlist_add || 0
            : artistData?.playlists_add || 0
        ),
        chartData: PlaylistsAddApple?.data?.length
          ? PlaylistsAddApple
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: AppleIcon,
        label: "Apple Music Charts",
        value: formatNumber(artistData?.total_charted_tracks || 0),
        score: formatNumber(artistData?.total_charted_tracks || 0),
        chartData: TotalChartedApple?.data?.length
          ? TotalChartedApple
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Best Performing Track",
        value: trackData?.artist_name,
        trackName: trackData?.track_name || "No track",
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: StartIcon,
        label: "iTunes Charts",
        value: period === "all" ? formatNumber(artistData?.total_charted_itunes || 0) : formatNumber(artistData?.charted_activities_itunes || 0),
        score: period === "all" ? formatNumber(artistData?.total_charted_itunes || 0) : formatNumber(artistData?.charted_activities_itunes || 0),
        chartData: TotalChartedITunes?.data?.length
          ? TotalChartedITunes
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FollowersIcon,
        label: "Total Playlists & Charts",
        value: formatNumber(artistData?.topPlaylistsCharts || 0),
        score: formatNumber(artistData?.topPlaylistsCharts || 0),
        chartData: ApplePlaylistsAndCharts?.data?.length
          ? ApplePlaylistsAndCharts
          : { name: "No Data", categories: [], data: [] },
      },
    ],
    [
      artistData,
      chartData,
      PlaylistsAddApple,
      TotalChartedApple,
      TotalChartedITunes,
      ApplePlaylistsAndCharts,
    ]
  );

  const AmazonMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Followers",
        value: formatNumber(artistData?.totalFollowers || 0),
        chartData: followersChartAmazon?.data?.length
          ? followersChartAmazon
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Total Charted",
        value: formatNumber(artistData?.total_charted || 0),
        chartData: TotalChartedAmazon?.data?.length
          ? TotalChartedAmazon
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Charted Tracks Total",
        value: formatNumber(artistData?.charted_tracks || 0),
        score: formatNumber(artistData?.charted_tracks || 0),
        chartData: { name: "No Data", categories: [], data: [] },
      },
      {
        icon: StreamIcon,
        label: "Playlists Add",
        value: formatNumber(artistData?.playlist_add || 0),
        chartData: TopPlaylistsAddAmazon?.data?.length
          ? TopPlaylistsAddAmazon
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FollowersIcon,
        label: "Total Playlists & Charts",
        value: formatNumber(artistData?.topPlaylistsCharts || 0),
        chartData: AmazonPlaylistsAndCharts?.data?.length
          ? AmazonPlaylistsAndCharts
          : { name: "No Data", categories: [], data: [] },
      },
    ],
    [
      artistData,
      chartData,
      followersChartAmazon,
      TotalChartedAmazon,
      TopPlaylistsAddAmazon,
      AmazonPlaylistsAndCharts,
    ]
  );

  const SoundCloudMetrics = useMemo(
    () => [
      {
        icon: StreamIcon,
        label: "Streams",
        value: formatNumber(artistData?.totalStreams || 0),
        chartData: streamChartSoundCloud?.data?.length
          ? streamChartSoundCloud
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Favorites",
        value: formatNumber(artistData?.total_favorites || 0),
        chartData: TotalFavoritesChart?.data?.length
          ? TotalFavoritesChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Best Performing Track",
        value: formatNumber(trackData?.streamsCount || 0),
        trackName: trackData?.track_name || "No track",
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: Listeners,
        label: "Reposts",
        value: formatNumber(artistData?.total_reposts || 0),
        chartData: totalRepostsChart?.data?.length
          ? totalRepostsChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FollowersIcon,
        label: "Followers",
        value: formatNumber(artistData?.totalFollowers || 0),
        chartData: followersChartSoundCloud?.data?.length
          ? followersChartSoundCloud
          : { name: "No Data", categories: [], data: [] },
      },
    ],
    [
      artistData,
      chartData,
      streamChartSoundCloud,
      followersChartSoundCloud,
      TotalChartedSoundCloud,
      TotalFavoritesChart,
    ]
  );
  switch (source?.toLowerCase()) {
    case "all":
      return allMetrics;
    case "spotify":
      return spotifyMetrics;
    case "apple_music":
      return AppleMusicMetrics;
    case "amazon":
      return AmazonMetrics;
    case "soundcloud":
      return SoundCloudMetrics;
    default:
      break;
  }
};

const useSocialPlatformData = (platformData, chartData, period, source) => {
  const trackData = platformData?.topTrack;

  const streamData = fillMissingValues(chartData?.totalStreams, 'streams_total')
  const streamChart = useMemo(
    () => ({
      name: "Total Streams Platform",
      categories: streamData?.map((entry) => entry.date) || [],
      data: streamData?.map((entry) => entry.streams_total) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const followersData = fillMissingValues(chartData?.totalFollowers, 'followers_total')
  const followersChart = useMemo(
    () => ({
      name: "Total Followers Platform",
      categories: followersData?.map((entry) => entry.date) || [],
      data:
        followersData?.map((entry) => entry.followers_total) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  // const averageEngagements = useMemo(
  //   () => ({
  //     name: "Average Engagements Platform",
  //     categories:
  //       chartData?.averageEngagement?.map((entry) => entry.date) || [],
  //     data:
  //       chartData?.averageEngagement?.map(
  //         (entry) => entry.average_engagement
  //       ) || [],
  //     intervalType: period,
  //   }),
  //   [chartData, period]
  // );

  const interactionsData = fillMissingValues(chartData?.totalInteractions, 'total_interactions')
  const interactions = useMemo(
    () => ({
      name: "Total Interactions Platform",
      categories:
        interactionsData?.map((entry) => entry.date) || [],
      data:
        interactionsData?.map(
          (entry) => entry.total_interactions
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const metricsAudience = useMemo(() => {
    return [
      {
        icon: FollowersIcon,
        label: "Followers",
        value: formatNumber(platformData?.totalFollowers || 0),
        chartData: followersChart?.data?.length
          ? followersChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Average Engagement",
        value: formatNumber(
          platformData?.averageEngagement
            ? platformData?.averageEngagement || 0
            : platformData?.totalEngagement || 0
        ),
        score: formatNumber(
          platformData?.averageEngagement
            ? platformData?.averageEngagement || 0
            : platformData?.totalEngagement || 0
        ),
        chartData: { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Best Performing Sound",
        value: formatNumber(trackData?.streamsCount || 0),
        trackName: trackData?.track_name || "No track",
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: Listeners,
        label: "Interactions",
        value: formatNumber(platformData?.totalInteractions || 0),
        chartData: interactions?.data?.length
          ? interactions
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: StreamIcon,
        label: (source === "youtube" && period === "all") ? "Channel + Video + Shorts Views" : (source === "youtube") ? "Video Views" : "Views",
        value: formatNumber(platformData?.totalStreams || 0),
        chartData: streamChart?.data?.length
          ? streamChart
          : { name: "No Data", categories: [], data: [] },
      },
    ];
  }, [platformData, chartData, streamChart, followersChart, interactions]);

  return metricsAudience;
};

const useCatalogData = (catalogData, chartData, period, selectedTrack) => {
  const trackData =
    "topTrackData" in (catalogData || {})
      ? catalogData?.topTrackData
      : catalogData?.topTrack;

  const listenersChart = useMemo(
    () => ({
      name: "Total listeners",
      categories:
        chartData?.totalListeners?.length > 0
          ? chartData?.totalListeners?.map((entry) => entry.date)
          : [],
      data:
        chartData?.totalListeners?.length > 0
          ? chartData?.totalListeners?.map(
            (entry) => entry.monthly_listeners_current
          )
          : [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const popularityScoreChart = useMemo(
    () => ({
      name: "Popularity Score",
      categories: chartData?.popularityScore?.map((entry) => entry.date) || [],
      data:
        chartData?.popularityScore?.map((entry) => entry.popularity_score) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const reachData = fillMissingValues(chartData?.totalReach, 'total_reach')
  const totalReach = useMemo(
    () => ({
      name: "Total Reach",
      categories: reachData?.map((entry) => entry.date) || [],
      data: reachData?.map((entry) => entry.total_reach) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const playlistData = fillMissingValues(chartData?.playlistAdd, 'playlist_add')
  const totalPlaylistsAdd = useMemo(
    () => ({
      name: "Playlists Add",
      categories: playlistData?.map((entry) => entry.date) || [],
      data: playlistData?.map((entry) => entry.playlist_add) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const playlistChartsData = fillMissingValues(chartData?.topPlaylistsCharts, 'topPlaylistsCharts')
  const trackPlaylistsCharts = useMemo(
    () => ({
      name: "Track Playlists",
      categories:
        playlistChartsData?.map((entry) => entry.date) || [],
      data:
        playlistChartsData?.map(
          (entry) => entry.topPlaylistsCharts
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const trackPlaylistAddData = fillMissingValues(chartData?.playlistAdd, 'playlist_add')
  const trackPlaylistsAdd = useMemo(
    () => ({
      name: "Track Playlists Add",
      categories: trackPlaylistAddData?.map((entry) => entry.date) || [],
      data: trackPlaylistAddData?.map((entry) => entry.playlist_add) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const artistMetrics = useMemo(
    () => [
      {
        icon: BestTrack,
        label: "Streams",
        value: formatNumber(trackData?.streamsCount || 0),
        trackName: trackData?.track_name || "No track",
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: FollowersIcon,
        label: "Popularity score",
        value: catalogData?.popularityScore + "%",
        chartData: popularityScoreChart?.data?.length
          ? popularityScoreChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: Listeners,
        label: "Listeners",
        value: catalogData?.totalListeners
          ? formatNumber(catalogData?.totalListeners || 0)
          : "",
        chartData: listenersChart?.data?.length
          ? listenersChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: Listeners,
        label: "Total Reach",
        value: formatNumber(catalogData?.totalReach || 0),
        score: formatNumber(catalogData?.totalReach || 0),
        chartData: totalReach?.data?.length
          ? totalReach
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: StreamIcon,
        label: "Playlists Adds",
        value: formatNumber(catalogData?.playlistAdd || 0),
        score: formatNumber(catalogData?.playlistAdd || 0),
        chartData: totalPlaylistsAdd?.data?.length
          ? totalPlaylistsAdd
          : { name: "No Data", categories: [], data: [] },
      },
    ],
    [
      catalogData,
      chartData,
      listenersChart,
      totalReach,
      totalPlaylistsAdd,
      period,
      trackData,
    ]
  );

  const trackMetrics = useMemo(
    () => [
      {
        icon: BestTrack,
        label: "Streams",
        value: formatNumber(trackData?.streamsCount || 0),
        trackName: trackData?.track_name || "No track",
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: FollowersIcon,
        label: "Popularity score",
        value: catalogData?.popularityScore + "%",
        chartData: popularityScoreChart?.data?.length
          ? popularityScoreChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: Listeners,
        label:
          period === "all" || period === "yearly"
            ? "Total Playlists"
            : period === "weekly" || period === "monthly"
              ? "Current Playlists"
              : "",
        value: formatNumber(catalogData?.playlistAdd || 0),
        chartData: trackPlaylistsCharts?.data?.length
          ? trackPlaylistsCharts
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: Listeners,
        label: "Total Reach",
        value: formatNumber(catalogData?.totalReach || 0),
        // score: formatNumber(catalogData?.totalReach || 0),
        chartData: totalReach?.data?.length
          ? totalReach
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: StreamIcon,
        label:
          period === "all"
            ? "Total Editorials"
            : period === "weekly"
              ? "Current Editorials"
              : "Playlists Adds",
        value: (period === "all" || period === "weekly") ? formatNumber(catalogData?.playlistEditorial || 0) : formatNumber(catalogData?.playlistAdd || 0),
        // score: formatNumber(catalogData?.playlistAdd || 0),
        chartData: trackPlaylistsAdd?.data?.length
          ? trackPlaylistsAdd
          : { name: "No Data", categories: [], data: [] },
      },
    ],
    [
      catalogData,
      chartData,
      trackPlaylistsAdd,
      totalReach,
      trackPlaylistsCharts,
      period,
      trackData,
      selectedTrack,
    ]
  );
  if (selectedTrack !== "") {
    return trackMetrics;
  } else {
    return artistMetrics;
  }
};

const getIconFromSource = (source) => {
  switch (source?.toLowerCase()) {
    case "spotify":
      return SpotifyLineIcon;
    case "apple_music":
      return AppleIcon;
    case "amazon":
      return AmazonIcon;
    case "itunes":
      return StartIcon;
    case "youtube":
      return YoutubeIcon;
    case "tiktok":
      return TiktokIcon;
    case "instagram":
      return InstaIcon;
    case "soundcloud":
      return SoundcloudIcon;
    case "radio":
      return RadioPlayIcon;
    default:
      return null;
  }
};


const getTopByClicks = (list = []) => {
  if (!list.length) return null;
  return [...list].sort((a, b) => b.clicks - a.clicks)[0];
};

const buildPieDatasets = (list = []) => ({
  clicks: list.map(i => ({ name: i.label, value: i.clicks })),
  impressions: list.map(i => ({ name: i.label, value: i.impressions })),
  spend: list.map(i => ({ name: i.label, value: i.spend })),
});
const useAudienceData = (audienceData, period, metaCampaignAudienceBreakdown,loadAudienceData,
    metaCampaignAudienceLoading) => {
  const topAge = useMemo(
    () => getTopByClicks(metaCampaignAudienceBreakdown?.age),
    [metaCampaignAudienceBreakdown]
  );

  const topGender = useMemo(
    () => getTopByClicks(metaCampaignAudienceBreakdown?.gender),
    [metaCampaignAudienceBreakdown]
  );

  const agePieDatasets = useMemo(
    () => buildPieDatasets(metaCampaignAudienceBreakdown?.age || []),
    [metaCampaignAudienceBreakdown]
  );

  const genderPieDatasets = useMemo(
    () => buildPieDatasets(metaCampaignAudienceBreakdown?.gender || []),
    [metaCampaignAudienceBreakdown]
  );
  const formatGender = (gender) => {
    if (!gender) return "No data";
    return `Top Gender- ${gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()}`;
  };
  const allMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Top Country",
        value: getCountryInfo(audienceData?.topCountry)?.name,
        flag: audienceData?.growthMarket,
        loading: loadAudienceData,
        chartData: [],
      },
      {
        icon: FanbaseIcon,
        label: "Top City",
        value: audienceData?.topCity,
        flag: audienceData?.growthMarket,
        loading: loadAudienceData,
        chartData: [],
      },
      {
        icon: UserIcon,
        label: "Top Ages",
        value: topAge?.label || "No data",
        score: topAge ? `${topAge.clicks} clicks` : "",
        loading: metaCampaignAudienceLoading,
        chartData: {
          type: "pie",
          defaultMetric: "clicks", // chart starts with clicks
          datasets: agePieDatasets,
        },
      },
      {
        icon: Listeners,
        label: "Gender Splits",
        value: formatGender(topGender?.label),
        score: topGender ? `${topGender.clicks} clicks` : "",
        loading: metaCampaignAudienceLoading,
        chartData: {
          type: "pie",
          defaultMetric: "clicks",
          datasets: genderPieDatasets,
        },
      },
      {
        icon: StreamIcon,
        label: audienceData?.trendMessage,
        value: getCountryInfo(audienceData?.growthMarket)?.name,
        flag: audienceData?.growthMarket,
        loading: loadAudienceData,
        chartData: [],
      },
    ],
    [
      audienceData,
      period,
      topAge,
      topGender,
      agePieDatasets,
      genderPieDatasets,
      loadAudienceData,
      metaCampaignAudienceLoading,
    ]
  );
  const currentMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Top Country",
        value: getCountryInfo(audienceData?.topCountry?.code)?.name,
        flag: audienceData?.topCountry?.code,
        loading: loadAudienceData,
        chartData: [],
      },
      {
        icon: FanbaseIcon,
        label: "Top City",
        value: audienceData?.topCity?.name,
        flag: audienceData?.topCity?.name,
        loading: loadAudienceData,
        chartData: [],
      },
      {
        icon: UserIcon,
        label: "Top Ages",
        value: topAge?.label || "No data",
        score: topAge ? `${topAge.clicks} clicks` : "",
        loading: metaCampaignAudienceLoading,
        chartData: {
          type: "pie",
          defaultMetric: "clicks", // chart starts with clicks
          datasets: agePieDatasets,
        },
      },
      {
        icon: Listeners,
        label: "Gender Splits",
        value: formatGender(topGender?.label),
        score: topGender ? `${topGender.clicks} clicks` : "",
        loading: metaCampaignAudienceLoading,
        chartData: {
          type: "pie",
          defaultMetric: "clicks",
          datasets: genderPieDatasets,
        },
      },
      {
        icon: StreamIcon,
        label:
          audienceData?.growthMarket?.status +
          " - " +
          audienceData?.growthMarket?.message,
        value: getCountryInfo(audienceData?.growthMarket?.market)?.name,
        flag: audienceData?.growthMarket?.market,
        loading: loadAudienceData,
        chartData: [],
      },
    ],
    [
      audienceData,
      period,
      topAge,
      topGender,
      agePieDatasets,
      genderPieDatasets,
      loadAudienceData,
      metaCampaignAudienceLoading,
    ]
  );
  if (period === "all") {
    return allMetrics;
  } else {
    return currentMetrics;
  }
};

const useArtistStats = (
  artistData,
  chartData,
  period,
  source,
  selectedTrack
) => {
  const trackData =
    "topTrackData" in (artistData || {})
      ? artistData?.topTrackData
      : artistData?.topTrack;

  const reachData = fillMissingValues(chartData?.totalReach, 'total_reach')
  const totalReachChart = useMemo(
    () => ({
      name: "Total Reach",
      categories: reachData?.map((entry) => entry.date) || [],
      data: reachData?.map((entry) => entry.total_reach) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const playlistsChartsData = fillMissingValues(chartData?.topPlaylistsCharts, 'topPlaylistsCharts')
  const totalPlaylistsCharts = useMemo(
    () => ({
      name: "Playlists and Charts",
      categories:
        playlistsChartsData?.map((entry) => entry.date) || [],
      data:
        playlistsChartsData?.map(
          (entry) => entry.topPlaylistsCharts
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const totalChartedApple = useMemo(
    () => ({
      name: "Total Charted",
      categories:
        (chartData?.total_charted || [])?.map((entry) => entry.date) || [],
      data:
        (chartData?.total_charted || [])?.map((entry) => entry.total_charted) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  // const totalFavoritesChart = useMemo(
  //   () => ({
  //     name: "Total Favorites SoundCloud",
  //     categories:
  //       (chartData?.total_favorites || [])?.map((entry) => entry.date) || [],
  //     data:
  //       (chartData?.total_favorites || [])?.map(
  //         (entry) => entry.total_favorites
  //       ) || [],
  //     intervalType: period,
  //   }),
  //   [chartData, period]
  // );

  // const totalRepostsChart = useMemo(
  //   () => ({
  //     name: "Total Reposts SoundCloud",
  //     categories:
  //       (chartData?.total_reposts || [])?.map((entry) => entry.date) || [],
  //     data:
  //       (chartData?.total_reposts || [])?.map((entry) => entry.reposts_total) ||
  //       [],
  //     intervalType: period,
  //   }),
  //   [chartData, period]
  // );

  // const totalPlaylistsAddAmazon = useMemo(
  //   () => ({
  //     name: "Top Playlists Adds Amazon",
  //     categories:
  //       (chartData?.playlist_add || [])?.map((entry) => entry.date) || [],
  //     data:
  //       (chartData?.playlist_add || [])?.map((entry) => entry.playlist_add) ||
  //       [],
  //     intervalType: period,
  //   }),
  //   [chartData, period]
  // );

  const trackPlaylistsChartsData = fillMissingValues(chartData?.topPlaylistsCharts, 'topPlaylistsCharts')
  const trackPlaylistsCharts = useMemo(
    () => ({
      name: "Track Playlists and Charts",
      categories:
        (trackPlaylistsChartsData || [])?.map((entry) => entry.date) || [],
      data:
        (trackPlaylistsChartsData || [])?.map(
          (entry) => entry.topPlaylistsCharts
        ) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const allMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Playlists & Charts",
        value: formatNumber(artistData?.topPlaylistsCharts || 0),
        chartData: totalPlaylistsCharts?.data?.length
          ? totalPlaylistsCharts
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Reach",
        value: formatNumber(artistData?.totalReach || 0),
        chartData: totalReachChart?.data?.length
          ? totalReachChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Most Milestones",
        value: formatNumber(artistData?.most_milestones || 0),
        trackName: trackData?.track_name,
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "Top Playlist",
        value:
          formatNumber(artistData?.topPlaylist?.followers || 0) + " Followers",
        url: artistData?.topPlaylist?.url,
        trackName: artistData?.topPlaylist?.name,
        image: artistData?.topPlaylist?.image
          ? artistData?.topPlaylist?.image
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
    ],
    [artistData, chartData, totalReachChart, totalPlaylistsCharts, trackData]
  );
  const spotifyMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Playlists & Charts",
        value: formatNumber(artistData?.newPlaylistsCharts || 0),
        chartData: totalPlaylistsCharts?.data?.length
          ? totalPlaylistsCharts
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Reach",
        value: formatNumber(artistData?.totalReach || 0),
        chartData: totalReachChart?.data?.length
          ? totalReachChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Most Milestones",
        value: formatNumber(artistData?.most_milestones || 0),
        trackName: trackData?.track_name,
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "Top Playlist",
        value:
          formatNumber(artistData?.topPlaylist?.followers || 0) + " Followers",
        url: artistData?.topPlaylist?.url,
        trackName: artistData?.topPlaylist?.name,
        image: artistData?.topPlaylist?.image
          ? artistData?.topPlaylist?.image
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
    ],
    [artistData, chartData, totalReachChart, totalPlaylistsCharts, trackData]
  );
  const appleMusicMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Playlists & Charts",
        value: formatNumber(artistData?.topPlaylistsCharts || 0),
        score: formatNumber(artistData?.topPlaylistsCharts || 0),
        chartData: totalPlaylistsCharts?.data?.length
          ? totalPlaylistsCharts
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Total Charts",
        value: formatNumber(artistData?.charted_activities || 0),
        score: formatNumber(artistData?.charted_activities || 0),
        chartData: totalChartedApple?.data?.length
          ? totalChartedApple
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Most Milestones",
        value: formatNumber(artistData?.most_milestones || 0),
        trackName: trackData?.track_name,
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "Charted Releases",
        value: formatNumber(artistData?.total_charted_tracks || 0),
        trackName: artistData?.topPlaylist?.playlist_name,
        image: LineChartIcon,
        // image: artistData?.topPlaylist?.image_url
        //   ? artistData?.topPlaylist?.image_url
        //   : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
    ],
    [artistData, chartData, totalChartedApple, totalPlaylistsCharts, trackData]
  );
  const iTunesMetrics = useMemo(
    () => [
      {
        icon: FanbaseIcon,
        label: "Total Charts",
        value:
          period === "all"
            ? formatNumber(artistData?.total_charted_itunes || 0)
            : formatNumber(artistData?.charted_activities || 0),
        score:
          period === "all"
            ? formatNumber(artistData?.total_charted_itunes || 0)
            : formatNumber(artistData?.charted_activities || 0),
        chartData: { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: selectedTrack && source === "itunes" ? "Milestones" : "Most Milestones",
        value: selectedTrack && source === "itunes" ? formatNumber((artistData?.most_milestones || 0) + (artistData?.charted_activities || 0)) : formatNumber(artistData?.most_milestones || 0),
        trackName: trackData?.track_name,
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "Charted Releases",
        value:
          period === "all"
            ? formatNumber(artistData?.total_charted_itunes || 0)
            : formatNumber(artistData?.total_charted_tracks || 0),
        image: LineChartIcon,
        // image: artistData?.topPlaylist?.image_url
        //   ? artistData?.topPlaylist?.image_url
        //   : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
    ],
    [artistData, chartData, trackData]
  );
  const appleMusicTrackMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Playlists & Charts",
        value: formatNumber(
          (artistData?.topPlaylistsCharts || 0) + artistData?.charted_activities
        ),
        score: formatNumber(
          (artistData?.topPlaylistsCharts || 0) + artistData?.charted_activities
        ),
        chartData: totalPlaylistsCharts?.data?.length
          ? totalPlaylistsCharts
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Total Charts",
        value: formatNumber(artistData?.charted_activities || 0),
        score: formatNumber(artistData?.charted_activities || 0),
        chartData: totalChartedApple?.data?.length
          ? totalChartedApple
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Milestones",
        value: formatNumber(
          (artistData?.most_milestones || 0) +
          (artistData?.charted_activities || 0)
        ),
        trackName: trackData?.track_name,
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "Top Playlist",
        // value: formatNumber(artistData?.topPlaylist?.followers || 0),
        url: artistData?.topPlaylist?.url,
        trackName: artistData?.topPlaylist?.name,
        image: artistData?.topPlaylist?.image
          ? artistData?.topPlaylist?.image
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
    ],
    [artistData, chartData, totalChartedApple, totalPlaylistsCharts, trackData]
  );
  // const amazonMetrics = useMemo(
  //   () => [
  //     {
  //       icon: FollowersIcon,
  //       label: "Playlists & Charts",
  //       value: formatNumber(artistData?.totalPlaylistsCharts || 0),
  //       chartData: totalPlaylistsCharts?.data?.length
  //         ? totalPlaylistsCharts
  //         : { name: "No Data", categories: [], data: [] },
  //     },
  //     {
  //       icon: FanbaseIcon,
  //       label: "Playlists Add",
  //       value: formatNumber(artistData?.playlist_add || 0),
  //       chartData: totalPlaylistsAddAmazon?.data?.length
  //         ? totalPlaylistsAddAmazon
  //         : { name: "No Data", categories: [], data: [] },
  //     },
  //     {
  //       icon: BestTrack,
  //       label: "Most Milestones",
  //       value: formatNumber(
  //         artistData?.most_milestones || artistData?.topPlaylistsCharts || 0
  //       ),
  //       trackName: trackData?.track_name,
  //       image: trackData?.image_url
  //         ? trackData?.image_url
  //         : require("../../../assets/images/best-track2.jpg"),
  //       chartData: [],
  //     },
  //     {
  //       icon: BestTrack,
  //       label: "Top Playlist",
  //       value: formatNumber(artistData?.topPlaylist?.followers || 0),
  //       trackName: artistData?.topPlaylist?.name,
  //       image: artistData?.topPlaylist?.image
  //         ? artistData?.topPlaylist?.image
  //         : require("../../../assets/images/best-track2.jpg"),
  //       chartData: [],
  //     },
  //   ],
  //   [
  //     artistData,
  //     chartData,
  //     totalPlaylistsAddAmazon,
  //     totalPlaylistsCharts,
  //     trackData,
  //   ]
  // );
  // const soundcloudMetrics = useMemo(
  //   () => [
  //     {
  //       icon: FollowersIcon,
  //       label: "Favorites",
  //       value: formatNumber(artistData?.total_favorites || 0),
  //       chartData: totalFavoritesChart?.data?.length
  //         ? totalFavoritesChart
  //         : { name: "No Data", categories: [], data: [] },
  //     },
  //     {
  //       icon: FanbaseIcon,
  //       label: "Reposts",
  //       value: formatNumber(artistData?.total_reposts || 0),
  //       chartData: totalRepostsChart?.data?.length
  //         ? totalRepostsChart
  //         : { name: "No Data", categories: [], data: [] },
  //     },
  //     {
  //       icon: BestTrack,
  //       label: "Most Milestones",
  //       value: formatNumber(artistData?.total_charted || 0),
  //       trackName: trackData?.track_name,
  //       image: trackData?.image_url
  //         ? trackData?.image_url
  //         : require("../../../assets/images/best-track2.jpg"),
  //       chartData: [],
  //     },
  //     {
  //       icon: BestTrack,
  //       label: "Top Playlist",
  //       value: formatNumber(artistData?.topPlaylist?.followers || 0),
  //       trackName: artistData?.topPlaylist?.name,
  //       image: artistData?.topPlaylist?.image
  //         ? artistData?.topPlaylist?.image
  //         : require("../../../assets/images/best-track2.jpg"),
  //       chartData: [],
  //     },
  //   ],
  //   [artistData, chartData, totalFavoritesChart, totalRepostsChart, trackData]
  // );
  const trackMetrics = useMemo(
    () => [
      {
        icon: FollowersIcon,
        label: "Playlists & Charts",
        value: formatNumber(artistData?.newPlaylistsCharts || 0),
        chartData: trackPlaylistsCharts?.data?.length
          ? trackPlaylistsCharts
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: FanbaseIcon,
        label: "Reach",
        value: formatNumber(artistData?.totalReach || 0),
        chartData: totalReachChart?.data?.length
          ? totalReachChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Milestones",
        value: formatNumber(artistData?.most_milestones || 0),
        trackName: trackData?.track_name,
        image: trackData?.image_url
          ? trackData?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "Top Playlist",
        value: formatNumber(artistData?.topPlaylist?.followers || 0),
        url: artistData?.topPlaylist?.url,
        trackName: artistData?.topPlaylist?.name,
        image: artistData?.topPlaylist?.image
          ? artistData?.topPlaylist?.image
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
    ],
    [artistData, chartData, totalReachChart, trackPlaylistsCharts, trackData]
  );

  if (selectedTrack && source === "all") {
    return trackMetrics;
  } else if (selectedTrack && source === "apple_music") {
    return appleMusicTrackMetrics;
  } else if (selectedTrack && source === "itunes") {
    return iTunesMetrics;
  } else {
    switch (source?.toLowerCase()) {
      case "all":
        return allMetrics;
      case "spotify":
        return spotifyMetrics;
      case "apple_music":
        return appleMusicMetrics;
      case "itunes":
        return iTunesMetrics;
      // case "amazon":
      //   return amazonMetrics;
      // case "soundcloud":
      //   return soundcloudMetrics;
      default:
        break;
    }
  }
};

const useRadioData = (
  radioData,
  chartData,
  audience,
  period,
  selectedTrack
) => {
  const radioChart = useMemo(
    () => ({
      name: "Radio Plays",
      categories: chartData?.total_plays?.map((entry) => entry.date) || [],
      data: chartData?.total_plays?.map((entry) => entry.total_plays) || [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const stationsChart = useMemo(
    () => ({
      name: "Total Stations",
      categories:
        chartData?.top_station_plays?.map((entry) => entry.date) || [],
      data:
        chartData?.top_station_plays?.map((entry) => entry.total_stations) ||
        [],
      intervalType: period,
    }),
    [chartData, period]
  );

  const metrics = useMemo(
    () => [
      {
        icon: StreamIcon,
        label: "Radio Plays",
        value: formatNumber(radioData?.total_plays || 0),
        chartData: radioChart?.data?.length
          ? radioChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Total Stations",
        value: formatNumber(radioData?.total_stations || 0),
        chartData: stationsChart?.data?.length
          ? stationsChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Most Played",
        value: formatNumber(radioData?.topTrack?.streamsCount || 0),
        trackName: radioData?.topTrack?.track_name || "No Track",
        image: radioData?.topTrack?.image_url
          ? radioData?.topTrack?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "All-Time Top Country",
        value: formatNumber(audience?.most_played || 0),
        trackName: getCountryInfo(audience?.top_country || "").name,
        flag: audience?.top_country,
        chartData: [],
      },
    ],
    [radioChart, stationsChart, radioData, chartData, audience, period]
  );

  const trackMetrics = useMemo(
    () => [
      {
        icon: StreamIcon,
        label: "Radio Plays",
        value: formatNumber(radioData?.total_plays || 0),
        chartData: radioChart?.data?.length
          ? radioChart
          : { name: "No Data", categories: [], data: [] },
      },
      {
        icon: BestTrack,
        label: "Top Station",
        value: formatNumber(radioData?.topRadioStation?.totalPlays || 0) + " Plays",
        trackName: radioData?.topRadioStation?.name || "No Station",
        flag: getCountryInfo(radioData?.topRadioStation?.topCountry).flag,
      },
      {
        icon: BestTrack,
        label: "Most Played",
        value: formatNumber(radioData?.topTrack?.streamsCount || 0) + " Plays",
        trackName: radioData?.topTrack?.track_name || "No Track",
        image: radioData?.topTrack?.image_url
          ? radioData?.topTrack?.image_url
          : require("../../../assets/images/best-track2.jpg"),
        chartData: [],
      },
      {
        icon: BestTrack,
        label: "All-Time Top Country",
        value: formatNumber(radioData?.country_data?.most_played || 0) + " Plays",
        trackName: getCountryInfo(radioData?.country_data?.top_country || "")
          .name,
        flag: getCountryInfo(radioData?.country_data?.top_country || "").flag,
        chartData: [],
      },
    ],
    [radioChart, stationsChart, radioData, chartData, audience, period]
  );
  if (selectedTrack !== "" && selectedTrack !== undefined) {
    return trackMetrics;
  } else {
    return metrics;
  }
};

const useReloadOnPopState = () => {
  useEffect(() => {
    const handlePopState = () => {
      window.location.reload(); // or trigger data re-fetch
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);
};

export {
  useMusicSourceData,
  useSocialPlatformData,
  useCatalogData,
  getIconFromSource,
  useAudienceData,
  useReloadOnPopState,
  useArtistStats,
  useRadioData,
};
